import React from 'react';
import { Select } from '@clockout-dev/design-elements';
import DateFilters from '../../common/DateFilters';
import {
    TRANSACTION_TYPE_FILTER_OPTIONS,
    ADVANCE_TYPE_FILTER_CHIP_OPTIONS,
    RECONCILIATION_FILTER_CHIP_OPTIONS,
    TRANSACTION_FILTER_ALL_VALUE,
    TRANSACTION_FILTER_LABELS,
    TRANSACTION_DATE_FORMAT,
} from '../reports.constants';
import { LEDGER_RECONCILIATION_FILTER } from '../../../constants/ledger.constants';
import styles from './styles/reports-transactions-filter-fields.module.scss';

/**
 * An end date before the start (or a start after the end) cannot be picked.
 * antd hands the other end of the range as `info.from` while the second
 * date is being chosen.
 */
function disabledCreatedDate(current, info) {
    if (!current || !info?.from) return false;
    return info.type === 'end' ? current.isBefore(info.from, 'day') : current.isAfter(info.from, 'day');
}

/**
 * Field group of the Transactions tab filter bar: transaction type, advance
 * type, reconciliation (three-state) and the created-date range (presets +
 * custom MM/DD/YYYY). Every option list and label comes from the constants —
 * no inline copy.
 *
 * The range is labelled "Created date" because that is what the service filters
 * on (`ledger.created_at`), never the processed date; the timezone the range is
 * evaluated in comes from the API payload and is named in the table's date headings.
 *
 * The fields hold no state and write nothing to the URL themselves: the
 * controller is the one writer (`writeTransactionFiltersToUrl`), so the
 * controlled `value`s here are the only source of what shows.
 *
 * @param {Object} props
 * @param {Object} props.filters the transaction filter model
 * @param {(name: string, value: any) => void} props.handleFilterChange
 * @param {(timeFrame: string) => void} props.handleTimeFrameChange
 * @param {(range: Array|null) => void} props.handleDateRangeChange
 * @param {boolean} [props.isMobile] stacked, labelled layout inside the modal
 */
function ReportsTransactionsFilterFields({
    filters,
    handleFilterChange,
    handleTimeFrameChange,
    handleDateRangeChange,
    isMobile = false,
}) {
    const fromAll = (value) => (value === TRANSACTION_FILTER_ALL_VALUE ? null : value);

    return (
        <>
            <Select
                label={isMobile ? TRANSACTION_FILTER_LABELS.TYPE : undefined}
                aria-label={TRANSACTION_FILTER_LABELS.TYPE}
                value={filters?.type || TRANSACTION_FILTER_ALL_VALUE}
                onChange={(value) => handleFilterChange?.('type', fromAll(value))}
                options={TRANSACTION_TYPE_FILTER_OPTIONS}
            />
            <Select
                label={isMobile ? TRANSACTION_FILTER_LABELS.ADVANCE_TYPE : undefined}
                aria-label={TRANSACTION_FILTER_LABELS.ADVANCE_TYPE}
                value={filters?.advanceType || TRANSACTION_FILTER_ALL_VALUE}
                onChange={(value) => handleFilterChange?.('advanceType', fromAll(value))}
                options={ADVANCE_TYPE_FILTER_CHIP_OPTIONS}
            />
            <Select
                label={isMobile ? TRANSACTION_FILTER_LABELS.RECONCILIATION : undefined}
                aria-label={TRANSACTION_FILTER_LABELS.RECONCILIATION}
                value={filters?.reconciliation || LEDGER_RECONCILIATION_FILTER.INCLUDE}
                onChange={(value) => handleFilterChange?.('reconciliation', value)}
                options={RECONCILIATION_FILTER_CHIP_OPTIONS}
            />
            <div className={isMobile ? styles.dateFiltersMobile : styles.dateFilters}>
                <DateFilters
                    label={TRANSACTION_FILTER_LABELS.CREATED_DATE}
                    labelSize="xs"
                    activeTimeFrame={filters?.timeFrame || 'all'}
                    dateRange={filters?.dateRange || null}
                    onTimeFrameChange={(value) => handleTimeFrameChange?.(value)}
                    onDateRangeChange={(range) => handleDateRangeChange?.(range)}
                    rangePickerProps={{
                        format: TRANSACTION_DATE_FORMAT,
                        disabledDate: disabledCreatedDate,
                        allowClear: false,
                        placeholder: [TRANSACTION_DATE_FORMAT, TRANSACTION_DATE_FORMAT],
                    }}
                />
            </div>
        </>
    );
}

export default ReportsTransactionsFilterFields;
